'use no memo';

import type { DirectoryPublic } from '@repo/types/directory';
import type { Permissions } from '@repo/types/globals';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { DownloadIcon, FolderSymlinkIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useFilesStorage } from '@/store/files-store';

export default function FsManageBar(fsManageBarProps: ComponentProps<'div'>) {
  const { className, ...props } = fsManageBarProps;
  const { resetSelected, selected, selectedSize, setOpenDelete, setOpenDownload, setOpenMove } = useFilesStorage();

  const size = selectedSize();

  const permissions = useMemo<Permissions>(() => {
    const selectedItems: DirectoryPublic[] = [...selected.directories, ...selected.files];
    if (selectedItems.length === 0) return { read: false, edit: false, write: false };

    return {
      read: selectedItems.every((el) => el.permissions.read),
      edit: selectedItems.every((el) => el.permissions.edit),
      write: selectedItems.every((el) => el.permissions.write),
    };
  }, [selected]);

  const handleOpenAction = (action: 'move' | 'download' | 'delete') => {
    switch (action) {
      case 'move':
        setOpenMove(true);
        break;
      case 'download':
        setOpenDownload(true);
        break;
      case 'delete':
        setOpenDelete(true);
        break;
    }
  };

  if (size === 0) return null;

  return (
    <div className={cn('border-border-primary bg-accent dark:bg-card flex items-center rounded-md border p-0.5', className)} {...props}>
      {/* Reset Selection Button */}
      <Button onClick={resetSelected} size="sm" variant="ghost" className="h-8.5 min-h-8.5 w-8.5 min-w-8.5">
        <XIcon />
      </Button>
      <p className="text-muted-foreground w-20 px-2 text-xs font-medium whitespace-nowrap">Wybrano: {size}</p>

      {/* Move Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button onClick={() => handleOpenAction('move')} disabled={!permissions.edit} size="sm" variant="ghost" className="h-8.5 min-h-8.5 w-8.5 min-w-8.5">
            <FolderSymlinkIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Przenieś</TooltipContent>
      </Tooltip>

      {/* Download Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button onClick={() => handleOpenAction('download')} disabled={!permissions.read} size="sm" variant="ghost" className="h-8.5 min-h-8.5 w-8.5 min-w-8.5">
            <DownloadIcon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Pobierz</TooltipContent>
      </Tooltip>

      {/* Delete Button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button onClick={() => handleOpenAction('delete')} disabled={!permissions.edit && !permissions.write} size="sm" variant="ghost" className="h-8.5 min-h-8.5 w-8.5 min-w-8.5">
            <Trash2Icon />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Usuń</TooltipContent>
      </Tooltip>
    </div>
  );
}
